import callAPI from 'api'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { Success, Fail } from 'store/types'
import { fileToBase64 } from 'utils/file'
import {
  CreateUserPayload,
  GetUserDetailResponse,
  GetUserDetailPayload,
  GetUserProfilesResponse,
  ApproveUserPayload,
  ApproveAllResponse,
  UpdateUserDetailPayload,
  GetUserProfilePermissionsResponse,
  GetUserProfilePermissionsPayload,
  FullPermissionKeys,
  GetPendingUsersResponse,
  PaginationPayload,
  UsersFilterResponse,
  GetUsersResponse,
  GetUsersPayload,
  CreateUserByAdminPayload,
  CreateUserProfilePayload,
  CreateUserProfileResponse,
  UpdateUserProfilePayload,
  UpdateUserProfileResponse,
  DeleteUserProfilePayload,
  DeleteUserProfileResponse,
  ResetPassword,
  AdminResetUserPasswordPayload,
  UpdateLanguagePreferencePayload,
  UpdateUserPayload,
  GetUserProfileReportPermissionsPayload,
  GetUserProfileReportPermissionsResponse,
  UpdateUserProfilePermissionPayload,
  UpdateUserProfilePermissionResponse,
  UpdateLeadDetailsVersionPayload,
  DownloadUsersToExcelResponse,
} from './types/user'

/**
 * Async function to create new user
 */
export const createUser = async (payload: CreateUserPayload) => {
  const response = await callAPI('crm_create_user', [payload])
  if (response.status === 'SUCCESS') {
    return response.data
  }
}

/**
 * Async function to fetch a user detail
 */
export const getUserDetailApi = async (payload: GetUserDetailPayload) =>
  await callAPI<GetUserDetailResponse, GetUserDetailPayload>('crm_get_user_details', [payload], {
    requireAuthorization: true,
  })

export const getUserDetail = createAsyncThunk<Fail | Success<GetUserDetailResponse>, GetUserDetailPayload>(
  'user/getUserDetail',
  getUserDetailApi,
)

/**
 * Async function to update user detail
 */
export const updateUserDetailApi = async (payload: UpdateUserPayload) =>
  await callAPI<GetUserDetailResponse, UpdateUserPayload>('crm_update_user_details', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const updateUserDetail = createAsyncThunk<Fail | Success<GetUserDetailResponse>, UpdateUserDetailPayload>(
  'user/updateUserDetail',
  async (payload: UpdateUserDetailPayload) =>
    await callAPI<GetUserDetailResponse, UpdateUserDetailPayload>('crm_update_user_details', [payload], {
      requireAuthorization: true,
      reloadOnReauth: false,
    }),
)

export const deleteProfilePhoto = (userId: string) =>
  callAPI<{ photo_filename: string; photo_url: string }, { file: null; user_id: string }>(
    'crm_update_user_photo',
    [{ file: null, user_id: userId }],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const getUserProfilesApi = async () =>
  await callAPI<GetUserProfilesResponse>('crm_get_user_profiles', [{}], { requireAuthorization: true })

export const getUserProfiles = createAsyncThunk<Fail | Success<GetUserProfilesResponse>>(
  'user/getUserProfiles',
  getUserProfilesApi,
)

export const uploadProfilePhoto = async (payload: File, userId: string) => {
  const converted = await fileToBase64(payload)
  return callAPI<{ photo_filename: string; photo_url: string }, { file: string; user_id: string }>(
    'crm_update_user_photo',
    [{ file: String(converted), user_id: userId }],
    { requireAuthorization: true, reloadOnReauth: false },
  )
}

export const getUserProfilePermissionsApi = async (payload: GetUserProfilePermissionsPayload) =>
  await callAPI<GetUserProfilePermissionsResponse, GetUserProfilePermissionsPayload>(
    'crm_get_user_profile_permissions',
    [payload],
    { requireAuthorization: true },
  )

export const getUserProfileReportPermissionsApi = async (payload: GetUserProfileReportPermissionsPayload) =>
  await callAPI<GetUserProfileReportPermissionsResponse, GetUserProfileReportPermissionsPayload>(
    'crm_get_user_profile_report_permissions',
    [payload],
    { requireAuthorization: true },
  )

export const getUserProfilePermissions = createAsyncThunk<
  Fail | Success<GetUserProfilePermissionsResponse>,
  GetUserProfilePermissionsPayload
>('user/getUserProfilePermissions', getUserProfilePermissionsApi)

export const getUserPermission = createAsyncThunk<Fail | Success<{ data: Array<FullPermissionKeys> }>>(
  'user/getUserPermissions',
  async () => {
    const response = await callAPI<{ data: Array<FullPermissionKeys> }>('crm_get_user_permissions', undefined, {
      requireAuthorization: true,
    })
    return response
  },
)

export const createUserProfile = async (payload: CreateUserProfilePayload) =>
  await callAPI<CreateUserProfileResponse, CreateUserProfilePayload>('crm_create_user_profile', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const updateUserProfile = async (payload: UpdateUserProfilePayload) =>
  await callAPI<UpdateUserProfileResponse, UpdateUserProfilePayload>('crm_update_user_profile', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const deleteUserProfile = async (payload: DeleteUserProfilePayload) =>
  await callAPI<DeleteUserProfileResponse, DeleteUserProfilePayload>('crm_delete_user_profile', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const updatePassword = async (curPassword: string, newPassword: string) =>
  await callAPI<{ success: boolean }, { current_password: string; new_password: string }>(
    'crm_update_user_password',
    [{ current_password: curPassword, new_password: newPassword }],
    { requireAuthorization: true, reloadOnReauth: false },
  )

export const resetPassword = async (payload: ResetPassword.Payload) =>
  await callAPI<ResetPassword.Response, ResetPassword.Payload>('crm_reset_user_password', [payload])

export const adminResetUserPassword = async (payload: AdminResetUserPasswordPayload) =>
  await callAPI<{ success: boolean }, AdminResetUserPasswordPayload>('crm_admin_reset_user_password', [payload], {
    requireAuthorization: true,
  })

export const getPendingUsers = createAsyncThunk<Fail | Success<GetPendingUsersResponse>, PaginationPayload>(
  'user/getPendingUsers',
  async (payload) =>
    await callAPI<GetPendingUsersResponse, PaginationPayload>('crm_get_pending_users', [payload], {
      requireAuthorization: true,
    }),
)

export const approveUser = async (payload: ApproveUserPayload) =>
  await callAPI<{ success: true }, { user_id: string }>('crm_approve_user', [payload], {
    requireAuthorization: true,
  })

export const declineUser = async (payload: { user_id: string }) =>
  await callAPI<{ success: true }, { user_id: string }>('crm_decline_user', [payload], {
    requireAuthorization: true,
  })

export const approveAllUsers = async () =>
  await callAPI<ApproveAllResponse>('crm_bulk_approve_user', undefined, {
    requireAuthorization: true,
  })

export const getUsersFilterApi = async () =>
  await callAPI<UsersFilterResponse>('crm_get_user_filters', undefined, {
    requireAuthorization: true,
  })

export const getUsersFilter = createAsyncThunk<Fail | Success<UsersFilterResponse>>(
  'user/getUsersFilter',
  getUsersFilterApi,
)

export const getUsersAPICall = async (payload: GetUsersPayload) =>
  await callAPI<GetUsersResponse, GetUsersPayload>('crm_get_users', [payload], { requireAuthorization: true })

export const getUsers = createAsyncThunk<Fail | Success<GetUsersResponse>, GetUsersPayload>(
  'user/getUsers',
  async (payload) => await getUsersAPICall(payload),
)

export const createUserByAdmin = async (payload: CreateUserByAdminPayload) =>
  await callAPI<Fail | Success<{ success: boolean }>, CreateUserByAdminPayload>('crm_create_user_by_admin', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const updateUserActiveStatus = async (payload: { is_active: boolean; user_id: string }) =>
  await callAPI<Fail | Success<{ success: true }>, { is_active: boolean; user_id: string }>(
    'crm_set_user_status',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const updateLanguagePreferences = async (payload: UpdateLanguagePreferencePayload) =>
  await callAPI<Fail | Success<{ success: boolean }>, UpdateLanguagePreferencePayload>(
    'update_language_preference',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )
export const updateLeadDetailsVersionPreferences = async (payload: UpdateLeadDetailsVersionPayload) =>
  await callAPI<Fail | Success<{ success: boolean }>, UpdateLeadDetailsVersionPayload>(
    'update_lead_detail_page_preference',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const updateUserProfileReportPermission = async (payload: UpdateUserProfilePermissionPayload) =>
  await callAPI<UpdateUserProfilePermissionResponse, UpdateUserProfilePermissionPayload>(
    'crm_update_user_profile_report_permissions',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const exportUsers = async () =>
  await callAPI<DownloadUsersToExcelResponse>('crm_download_users_to_excel', undefined, { requireAuthorization: true })
